// src/Dashboard/admin/sections/SettingsSection.jsx
import { useEffect, useMemo, useState } from "react";
import { Settings, Save, Shield, AlertTriangle, CheckCircle2, XCircle, Wrench, Mail, MessageSquare } from "lucide-react";

function safeNumber(x, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function Badge({ ok, labelOk = "Ready", labelBad = "Not ready" }) {
  return ok ? (
    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold border bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800">
      <CheckCircle2 size={14} /> {labelOk}
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800">
      <XCircle size={14} /> {labelBad}
    </span>
  );
}

/**
 * settings shape (recommended):
 * {
 *   default_pass_mark: 80,
 *   default_attempt_limit: 3,
 *   certificates_enabled: true
 * }
 *
 * providerStatus shape (already in your comms UI):
 * {
 *   sms: { ok: true, message?: "..." },
 *   email: { ok: false, message?: "Missing API key" }
 * }
 */
export default function SettingsSection({
  loading,
  error,

  // data
  settings,
  providerStatus,

  // actions
  onRefresh,
  onSave, // async (nextSettings) => void

  // permissions
  readOnly = true,
}) {
  const [draft, setDraft] = useState({
    default_pass_mark: 80,
    default_attempt_limit: 3,
    certificates_enabled: true,
  });

  useEffect(() => {
    if (!settings) return;
    setDraft((d) => ({
      ...d,
      default_pass_mark: safeNumber(settings.default_pass_mark, 80),
      default_attempt_limit: safeNumber(settings.default_attempt_limit, 3),
      certificates_enabled: !!settings.certificates_enabled,
    }));
  }, [settings]);

  const canSave = !readOnly && typeof onSave === "function";

  const smsOk = !!providerStatus?.sms?.ok;
  const emailOk = !!providerStatus?.email?.ok;

  const dirty = useMemo(() => {
    if (!settings) return true;
    return (
      safeNumber(settings.default_pass_mark, 80) !== safeNumber(draft.default_pass_mark, 80) ||
      safeNumber(settings.default_attempt_limit, 3) !== safeNumber(draft.default_attempt_limit, 3) ||
      !!settings.certificates_enabled !== !!draft.certificates_enabled
    );
  }, [settings, draft]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10">
              <Settings size={20} className="text-orange-600 dark:text-orange-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">System Settings</h2>
              <p className="text-xs text-slate-500">
                Configure defaults and verify provider health. (These should be enforced server-side.)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Refresh
            </button>

            <button
              onClick={async () => {
                if (!canSave) return;
                await onSave(draft);
              }}
              disabled={!canSave || !dirty || loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60"
              title={readOnly ? "Enable backend endpoints to save settings" : "Save settings"}
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </div>

        {readOnly ? (
          <div className="mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 text-sm dark:bg-slate-950 dark:border-slate-800 dark:text-slate-200">
            This settings UI is ready. Saving is currently <span className="font-semibold">read-only</span> until the backend settings endpoint is wired.
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
            {error}
          </div>
        ) : null}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Defaults */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60">
              <Shield size={18} className="text-slate-700 dark:text-slate-200" />
            </div>
            <div>
              <div className="text-lg font-semibold">Defaults</div>
              <div className="text-xs text-slate-500">Applied to new courses unless overridden per-course (later).</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Default pass mark (%)">
              <input
                type="number"
                min={0}
                max={100}
                value={draft.default_pass_mark}
                onChange={(e) => setDraft((d) => ({ ...d, default_pass_mark: safeNumber(e.target.value, 0) }))}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                disabled={!canSave && readOnly}
              />
            </Field>

            <Field label="Default attempt limit">
              <input
                type="number"
                min={1}
                max={50}
                value={draft.default_attempt_limit}
                onChange={(e) => setDraft((d) => ({ ...d, default_attempt_limit: safeNumber(e.target.value, 1) }))}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                disabled={!canSave && readOnly}
              />
            </Field>

            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!draft.certificates_enabled}
                  onChange={(e) => setDraft((d) => ({ ...d, certificates_enabled: e.target.checked }))}
                  className="w-4 h-4"
                  disabled={!canSave && readOnly}
                />
                Enable certificates
              </label>
              <div className="text-xs text-slate-500 mt-1">
                If disabled, certificate generation endpoints should refuse to generate/issue certificates.
              </div>
            </div>
          </div>
        </div>

        {/* Provider health */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="text-lg font-semibold mb-4">Provider Health</div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <MessageSquare size={18} />
                  <div className="font-semibold">SMS</div>
                </div>
                <Badge ok={smsOk} />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {providerStatus?.sms?.message || (smsOk ? "Provider is configured." : "Provider is not configured.")}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Mail size={18} />
                  <div className="font-semibold">Email</div>
                </div>
                <Badge ok={emailOk} />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {providerStatus?.email?.message || (emailOk ? "Provider is configured." : "Provider is not configured.")}
              </div>
            </div>

            <div className="text-xs text-slate-500">
              Tip: These statuses can be loaded from the same comms meta endpoint you already use.
            </div>
          </div>
        </div>
      </div>

      {/* Maintenance + Danger zone */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60">
              <Wrench size={18} />
            </div>
            <div>
              <div className="text-lg font-semibold">Maintenance</div>
              <div className="text-xs text-slate-500">Safe tools (read-only for now).</div>
            </div>
          </div>

          <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-2 list-disc pl-5">
            <li>Rebuild analytics cache (optional RPC)</li>
            <li>Recompute certificates (by course)</li>
            <li>Sync hierarchy data</li>
          </ul>

          <div className="mt-4 text-xs text-slate-500">
            When ready, we can wire these to protected admin endpoints and show progress logs.
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-xl bg-red-50 dark:bg-red-900/20">
              <AlertTriangle size={18} className="text-red-700 dark:text-red-200" />
            </div>
            <div>
              <div className="text-lg font-semibold">Danger Zone</div>
              <div className="text-xs text-slate-500">High-impact actions (should require confirm + server permission).</div>
            </div>
          </div>

          <div className="space-y-3">
            <DangerButton disabled title="Deactivate dormant learners">
              Bulk deactivate by inactivity threshold (later).
            </DangerButton>

            <DangerButton disabled title="Delete old logs">
              Purge comms/reminders logs older than X days (later).
            </DangerButton>
          </div>

          <div className="mt-4 text-xs text-slate-500">
            These are intentionally disabled until endpoints + confirmations are added.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">{label}</label>
      {children}
    </div>
  );
}

function DangerButton({ title, children, disabled }) {
  return (
    <button
      disabled={disabled}
      className={`w-full text-left rounded-2xl border p-4 ${
        disabled
          ? "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-500 cursor-not-allowed"
          : "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 hover:opacity-90"
      }`}
      title={disabled ? "Wire backend first" : title}
    >
      <div className="font-semibold">{title}</div>
      <div className="text-xs mt-1 opacity-80">{children}</div>
    </button>
  );
}
